import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as toursService from '../services/toursService';
import { TOURS_KEY } from '../utils/query-keys';
import type { TourName } from '../tooltips/copy';

/**
 * Read the per-user tour-completion map. Always enabled while authenticated
 * (assumed via the axios interceptor attaching the JWT); server returns
 * `{}` for fresh users. React Query caches this so the <Tour> wrapper can
 * synchronously decide whether to fire without a network round-trip.
 */
export const useTours = () =>
  useQuery({
    queryKey: TOURS_KEY,
    queryFn: async () => {
      const res = await toursService.getTours();
      // Backend wraps payload in { code, message, result }; align with other services
      return (res as any)?.result?.tours_seen ?? {};
    },
    staleTime: 5 * 60 * 1000, // 5 min — tours-seen doesn't change often
  });

/**
 * Mark a tour completed. Idempotent server-side; safe to call multiple times.
 * Invalidates TOURS_KEY so the next useTours read reflects the new state
 * (prevents the tour from re-firing on the same device).
 */
export const useMarkTourComplete = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: TourName) => toursService.markTourComplete(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TOURS_KEY });
    },
  });
};
